import React, { useEffect, useState } from 'react';
import { Container, Paper, Typography, Button, Box, Alert, Divider } from '@mui/material';
import { AdminPanelSettings, Create, School, ArrowBack } from '@mui/icons-material';
import GoogleIcon from '@mui/icons-material/Google';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const BRAND_GRADIENT = 'linear-gradient(to right, #00CCCC, #FFC840, #FFB000, #CD00FF, #DD50FF, #00B2AD)';

type RoleKey = 'learner' | 'admin' | 'content-manager';

const ROLE_CONFIG: Record<RoleKey, {
  label: string;
  authRole: string;
  description: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}> = {
  learner: {
    label: 'Learner',
    authRole: 'learner',
    description: 'Access your courses, track progress, and earn certificates.',
    color: '#1976d2',
    bg: '#E3F2FD',
    icon: <School sx={{ fontSize: 32 }} />,
  },
  admin: {
    label: 'Admin',
    authRole: 'admin',
    description: 'Manage users, monitor analytics, and oversee the platform.',
    color: '#7b1fa2',
    bg: '#F3E5F5',
    icon: <AdminPanelSettings sx={{ fontSize: 32 }} />,
  },
  'content-manager': {
    label: 'Content Manager',
    authRole: 'content_manager',
    description: 'Create and publish courses, modules, and assessments.',
    color: '#2e7d32',
    bg: '#E8F5E9',
    icon: <Create sx={{ fontSize: 32 }} />,
  },
};

const RoleLoginPage: React.FC = () => {
  const { role } = useParams<{ role: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cfg = ROLE_CONFIG[role as RoleKey];

  // Redirect if unknown role
  useEffect(() => {
    if (!cfg) navigate('/login', { replace: true });
  }, [cfg, navigate]);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  if (!cfg) return null;

  // Redirect to Google OAuth — stores role in sessionStorage so the
  // /auth/callback page knows which role to assign after the flow
  const handleGoogleRedirect = async () => {
    setLoading(true);
    setError(null);
    try {
      sessionStorage.setItem('oauth_role', cfg.authRole);
      const response = await authAPI.getAuthUrls();
      window.location.href = response.data.google;
    } catch {
      setError('Failed to start Google sign-in. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Brand */}
        <Typography variant="h4" fontWeight={900} mb={1}
          sx={{ background: BRAND_GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
          Sedstart
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          Learning Management Platform
        </Typography>

        <Paper elevation={3} sx={{ width: '100%', borderRadius: 3, overflow: 'hidden' }}>
          {/* Coloured role header */}
          <Box sx={{ bgcolor: cfg.bg, borderBottom: `3px solid ${cfg.color}`, p: 3, textAlign: 'center' }}>
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%', bgcolor: cfg.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 1.5, color: '#fff',
            }}>
              {cfg.icon}
            </Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: cfg.color }}>
              {cfg.label} Sign In
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {cfg.description}
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleRedirect}
              disabled={loading}
              sx={{
                py: 1.5,
                justifyContent: 'flex-start',
                pl: 3,
                bgcolor: cfg.color,
                '&:hover': { filter: 'brightness(0.9)', bgcolor: cfg.color },
                fontWeight: 700,
              }}
            >
              {loading ? 'Signing in…' : 'Continue with Google'}
            </Button>

            <Divider sx={{ my: 3 }} />

            <Button
              fullWidth
              variant="text"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/login')}
              sx={{ color: 'text.secondary' }}
            >
              Back to role selection
            </Button>
          </Box>
        </Paper>

        <Typography variant="caption" color="text.disabled" mt={3} textAlign="center">
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </Typography>
      </Box>
    </Container>
  );
};

export default RoleLoginPage;
